import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App as AntdApp, ConfigProvider, Spin, theme as antdTheme, unstableSetRender } from 'antd';
import enUS from 'antd/locale/en_US';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en';
import '@ant-design/v5-patch-for-react-19';
import { ComponentAuth } from './components/Auth';
import { ComponentMainWrapper } from './components/MainWrapper';
import { DensityProvider, useDensity } from './context/DensityContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import PageNotFound from './pages/_NotFound';
import { PageMFA, PageSignIn } from './pages/Auth';
import { darkThemeConfig, themeConfig } from './theme';
import './styles/app.scss';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.locale('en');

const PageBrands = React.lazy(() => import('./pages/Brands'));
const PageKCP = React.lazy(() => import('./pages/KCP'));
const PageNotifications = React.lazy(() => import('./pages/Notifications'));
const PageSystem = React.lazy(() => import('./pages/System'));

const queryClient = new QueryClient();

/**
 * antd v5 compatibility with React 16 ~ 18 by default. For React 19, you can use the following compatibility methods to adapt.
 */
unstableSetRender((node, container) => {
    container._reactRoot ||= createRoot(container);
    const root = container._reactRoot;
    root.render(node);
    return async () => {
        await new Promise((resolve) => setTimeout(resolve, 0));
        root.unmount();
    };
});

const SuspenseFallback = () => (
    <div className='placeholder'>
        <div className='placeholder-spinner'>
            <Spin size='large' />
        </div>
    </div>
);

const ThemedApp = ({ children }) => {
    const { density } = useDensity();
    const { resolved } = useTheme();
    const base = resolved === 'dark' ? darkThemeConfig : themeConfig;
    const compact = density === 'compact' ? [antdTheme.compactAlgorithm] : [];
    const darkAlg = resolved === 'dark' ? [antdTheme.darkAlgorithm] : [];
    const algorithm = [...darkAlg, ...compact];
    return (
        <ConfigProvider theme={{ ...base, algorithm }} locale={enUS}>
            <AntdApp>{children}</AntdApp>
        </ConfigProvider>
    );
};

const App = () => (
    <ThemeProvider>
        <DensityProvider>
            <ThemedApp>
                <QueryClientProvider client={queryClient}>
                    <Router>
                        <Routes>
                            <Route path='/sign-in/*' element={<PageSignIn />} />
                            <Route
                                path='*'
                                element={
                                    <ComponentAuth>
                                        <ComponentMainWrapper>
                                            <Suspense fallback={<SuspenseFallback />}>
                                                <Routes>
                                                    <Route path='mfa/*' element={<PageMFA />} />

                                                    <Route path='/' element={<Navigate replace to='/brands-v2' />} />
                                                    <Route
                                                        path='brands/*'
                                                        element={<Navigate replace to='/brands-v2' />}
                                                    />
                                                    <Route path='brands-v2/*' element={<PageBrands />} />
                                                    <Route path='kcp/*' element={<PageKCP />} />
                                                    <Route path='notifications/*' element={<PageNotifications />} />
                                                    <Route path='system/*' element={<PageSystem />} />
                                                    <Route path='*' element={<PageNotFound />} />
                                                </Routes>
                                            </Suspense>
                                        </ComponentMainWrapper>
                                    </ComponentAuth>
                                }
                            />
                        </Routes>
                    </Router>
                </QueryClientProvider>
            </ThemedApp>
        </DensityProvider>
    </ThemeProvider>
);

const container = document.getElementById('klickly-admin-root');
const root = createRoot(container);
root.render(<App />);
